import React from "react";
import {
  getInvestmentLimitHelperText,
  resolveStrategyInvestmentLimits,
} from "./ispFundingThresholds";
import { normalizeWholePercentageInput } from "./ispBusinessRules";
import { IspRegistrationOptions } from "./ispRegistrationOptions";
import {
  MMMS_ACCOUNT_GOALS,
  STANDARD_ACCOUNT_GOALS,
  getStandardRegistrationValue,
  getStandardRegistrationBondOptions,
  getStandardRegistrationRowIssue,
} from "./standardRegistrationUtils";

const formatCurrency = (value) => {
  const number = Number(String(value || "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(number) ? `$${Math.round(number).toLocaleString()}` : "";
};

const accountGoalId = (rowIndex, goal) =>
  `standardMultiGoal${rowIndex}${goal.replace(/[^A-Za-z0-9]/g, "")}`;

const StandardMultiRegistrationEditor = ({
  rows = [],
  strategy,
  proposalTypes = [],
  registrationTypes = [],
  registrationCategoryTypes = [],
  existingRequestAction = "",
  modelSelection = "",
  pathStrategy = "",
  jointClients = [],
  prefillGeneration = 0,
  fundingAmountHistory = [],
  identityReadOnly = false,
  onRowChange = () => {},
  onAddRegistration = () => {},
  onRemoveRegistration = () => {},
}) => {
  const proposalType = proposalTypes.find(
    (candidate) => candidate?.name === strategy,
  );
  const investmentLimits = resolveStrategyInvestmentLimits(
    strategy,
    proposalType,
  );
  const isManagedMoneyMarket = String(strategy || "")
    .toLowerCase()
    .includes("managed money market solution");
  const goals = isManagedMoneyMarket
    ? MMMS_ACCOUNT_GOALS
    : STANDARD_ACCOUNT_GOALS;

  return (
    <div className="form-group" data-testid="standard-multi-registration-editor">
      <label className="d-block mb-1">
        <strong>Registrations</strong>
      </label>
      <small className="form-text text-muted mb-3">
        Enter each account holder and Registration Type. The selected Strategy,
        Risk Tolerance, and fee schedule apply to every registration; the
        questions within each registration apply only to that row.
      </small>
      {!identityReadOnly && (
        <datalist id="standardMultiAccountHolderNames">
          {jointClients.map((client, index) => (
            <option
              key={`${client?.clientName || "client"}-${index}`}
              value={client?.clientName || ""}
            />
          ))}
        </datalist>
      )}
      <datalist id="standardMultiInvestmentAmounts">
        {fundingAmountHistory.map((amount) => (
          <option value={amount} key={amount} />
        ))}
      </datalist>

      {rows.map((row, index) => {
        const rowKey = row?._rowId || row?.registration || `registration${index}`;
        const rowRegistration = getStandardRegistrationValue(row);
        const bondOptions = getStandardRegistrationBondOptions({
          registration: rowRegistration,
          registrationTypes,
          strategy,
          proposalTypes,
          pathStrategy,
          modelSelection,
        });
        const issue = getStandardRegistrationRowIssue(row, {
          strategy,
          proposalTypes,
          registrationTypes,
          pathStrategy,
          investmentLimits,
          existingRequestAction,
        });
        const accountGoals = Array.isArray(row.accountGoal)
          ? row.accountGoal
          : row.accountGoal
            ? [row.accountGoal]
            : [];
        const otherGoalSelected = isManagedMoneyMarket
          ? accountGoals.includes("Other")
          : row.accountGoal === "Funding for another goal";
        const investmentNumber = Number(
          String(row.initialInvestmentAmount || "").replace(/[^0-9.-]+/g, ""),
        );
        const incomeNumber = Number(
          String(row.accountIncomeAmount || "").replace(/[^0-9.-]+/g, ""),
        );
        const incomeExceedsInvestment =
          row.accountIncomeAmount &&
          Number.isFinite(investmentNumber) &&
          investmentNumber > 0 &&
          incomeNumber > investmentNumber;
        const todayIso = new Date(
          Date.now() - new Date().getTimezoneOffset() * 60000,
        )
          .toISOString()
          .split("T")[0];
        const minEndIso = row.accountIncomeStartDate
          ? new Date(
              new Date(`${row.accountIncomeStartDate}T00:00:00`).getTime() +
                86400000,
            )
              .toISOString()
              .split("T")[0]
          : undefined;
        const setField = (field, value) => onRowChange(rowKey, field, value);

        return (
          <div
            key={rowKey}
            className="mb-4 p-3"
            data-testid={`standard-multi-registration-row-${index}`}
            style={{
              border: "1px solid #dee2e6",
              borderLeft: `5px solid ${issue ? "#ced4da" : "green"}`,
              borderRadius: "4px",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Registration {index + 1}</strong>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => onRemoveRegistration(index)}
                disabled={rows.length <= 1}
                aria-label={`Remove registration ${index + 1}`}
              >
                <i className="fa fa-trash" aria-hidden="true" />
              </button>
            </div>

            {identityReadOnly ? (
              <div className="alert alert-light py-2 mb-3" role="status">
                {[row.accountOwner, row.accountLast4 && `****${row.accountLast4}`, rowRegistration]
                  .filter(Boolean)
                  .join(" | ")}
              </div>
            ) : (
              <div className="form-row">
                <div className="form-group col-md-5">
                  <label htmlFor={`standardMultiOwner${index}`}>
                    Account Holder Name
                  </label>
                  <input
                    id={`standardMultiOwner${index}`}
                    name={`isp-standard-account-owner-${index}-v${prefillGeneration}`}
                    type="text"
                    autoComplete="off"
                    className="form-control"
                    list="standardMultiAccountHolderNames"
                    maxLength={160}
                    value={row.accountOwner || ""}
                    onChange={(event) => setField("accountOwner", event.target.value)}
                  />
                </div>
                <div className="form-group col-md-7">
                  <label htmlFor={`standardMultiRegistration${index}`}>
                    Registration Type
                  </label>
                  <select
                    id={`standardMultiRegistration${index}`}
                    className="form-control"
                    value={rowRegistration}
                    onChange={(event) => setField("value", event.target.value)}
                  >
                    <IspRegistrationOptions
                      registrationTypes={registrationTypes}
                      registrationCategoryTypes={registrationCategoryTypes}
                      selectedValue={rowRegistration}
                    />
                  </select>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group col-md-6">
                <label htmlFor={`standardMultiInvestment${index}`}>
                  Initial Amount of Investment
                </label>
                <input
                  id={`standardMultiInvestment${index}`}
                  name={`isp-standard-investment-${index}-v${prefillGeneration}`}
                  type="text"
                  inputMode="numeric"
                  list="standardMultiInvestmentAmounts"
                  autoComplete="off"
                  className="form-control"
                  value={row.initialInvestmentAmount || ""}
                  onChange={(event) =>
                    setField(
                      "initialInvestmentAmount",
                      event.target.value.replace(/[^0-9]/g, ""),
                    )
                  }
                  onFocus={(event) => {
                    event.target.value = String(row.initialInvestmentAmount || "").replace(
                      /[^0-9.-]+/g,
                      "",
                    );
                  }}
                  onBlur={(event) =>
                    setField("initialInvestmentAmount", formatCurrency(event.target.value))
                  }
                  placeholder="$0"
                />
                {investmentLimits && (
                  <small className="form-text text-muted">
                    {getInvestmentLimitHelperText(investmentLimits)}
                  </small>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor={`standardMultiNetWorth${index}`}>
                  % of Net Worth
                </label>
                <div className="input-group">
                  <input
                    id={`standardMultiNetWorth${index}`}
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="100"
                    step="1"
                    className="form-control"
                    value={row.accountNetWorthPct || ""}
                    onChange={(event) => {
                      const normalized = normalizeWholePercentageInput(
                        event.target.value,
                      );
                      if (
                        normalized !== null &&
                        (normalized === "" || Number(normalized) <= 100)
                      ) {
                        setField("accountNetWorthPct", normalized);
                      }
                    }}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="d-block">Need for Income</label>
              <small className="form-text text-muted mb-2">
                Separate documentation is required to establish a distribution plan.
              </small>
              {["Yes", "No"].map((answer) => (
                <div className="form-check form-check-inline" key={answer}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`standardMultiIncomeNeed${index}`}
                    id={`standardMultiIncomeNeed${answer}${index}`}
                    value={answer}
                    checked={row.accountIncomeNeed === answer}
                    onChange={(event) => setField("accountIncomeNeed", event.target.value)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`standardMultiIncomeNeed${answer}${index}`}
                  >
                    {answer}
                  </label>
                </div>
              ))}
            </div>

            {row.accountIncomeNeed === "Yes" && (
              <>
                <div className="form-row">
                  <div className="form-group col-md-4">
                    <label htmlFor={`standardMultiIncomeAmount${index}`}>
                      Dollar Amount
                    </label>
                    <input
                      id={`standardMultiIncomeAmount${index}`}
                      type="text"
                      inputMode="numeric"
                      className="form-control"
                      value={row.accountIncomeAmount || ""}
                      onChange={(event) =>
                        setField(
                          "accountIncomeAmount",
                          event.target.value.replace(/[^0-9]/g, ""),
                        )
                      }
                      onFocus={(event) => {
                        event.target.value = String(row.accountIncomeAmount || "").replace(
                          /[^0-9.-]+/g,
                          "",
                        );
                      }}
                      onBlur={(event) =>
                        setField("accountIncomeAmount", formatCurrency(event.target.value))
                      }
                      placeholder="$0"
                    />
                    {incomeExceedsInvestment && (
                      <small className="form-text text-danger">
                        Cannot exceed the initial investment amount.
                      </small>
                    )}
                  </div>
                  <div className="form-group col-md-4">
                    <label htmlFor={`standardMultiIncomeStart${index}`}>Start Date</label>
                    <input
                      id={`standardMultiIncomeStart${index}`}
                      type="date"
                      min={todayIso}
                      className="form-control"
                      value={row.accountIncomeStartDate || ""}
                      onChange={(event) => {
                        setField("accountIncomeStartDate", event.target.value);
                        if (
                          row.accountIncomeEndDate &&
                          event.target.value >= row.accountIncomeEndDate
                        ) {
                          setField("accountIncomeEndDate", "");
                        }
                      }}
                    />
                  </div>
                  <div className="form-group col-md-4">
                    <label htmlFor={`standardMultiIncomeEnd${index}`}>
                      End Date <em>(optional)</em>
                    </label>
                    <input
                      id={`standardMultiIncomeEnd${index}`}
                      type="date"
                      min={minEndIso}
                      className="form-control"
                      value={row.accountIncomeEndDate || ""}
                      onChange={(event) => setField("accountIncomeEndDate", event.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="d-block">Frequency</label>
                  {["Monthly", "Quarterly", "Semiannually", "Annually"].map(
                    (frequency) => (
                      <div className="form-check form-check-inline" key={frequency}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name={`standardMultiIncomeFrequency${index}`}
                          id={`standardMultiIncomeFrequency${frequency}${index}`}
                          value={frequency}
                          checked={row.accountIncomeFrequency === frequency}
                          onChange={(event) =>
                            setField("accountIncomeFrequency", event.target.value)
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`standardMultiIncomeFrequency${frequency}${index}`}
                        >
                          {frequency}
                        </label>
                      </div>
                    ),
                  )}
                </div>
              </>
            )}

            <div className="form-group">
              <label className="d-block">Primary Goal</label>
              {goals.map((goal) => {
                const id = accountGoalId(index, goal);
                const checked = isManagedMoneyMarket
                  ? accountGoals.includes(goal)
                  : row.accountGoal === goal;
                return (
                  <div
                    className="form-check standard-multi-goal-option mb-1"
                    key={goal}
                  >
                    <input
                      className="form-check-input"
                      type={isManagedMoneyMarket ? "checkbox" : "radio"}
                      name={`standardMultiGoal${index}`}
                      id={id}
                      value={goal}
                      checked={checked}
                      onChange={(event) => {
                        if (isManagedMoneyMarket) {
                          const next = event.target.checked
                            ? [...accountGoals, goal]
                            : accountGoals.filter((candidate) => candidate !== goal);
                          setField("accountGoal", next);
                          if (goal === "Other" && !event.target.checked) {
                            setField("accountGoalOther", "");
                          }
                        } else {
                          setField("accountGoal", event.target.value);
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor={id}>
                      {goal === "Other" ? "Other:" : goal}
                    </label>
                  </div>
                );
              })}
              {otherGoalSelected && (
                <input
                  type="text"
                  className="form-control mt-2"
                  aria-label={`Registration ${index + 1} Goal Explanation`}
                  maxLength={200}
                  value={row.accountGoalOther || ""}
                  onChange={(event) => setField("accountGoalOther", event.target.value)}
                  placeholder="Please explain"
                />
              )}
            </div>

            {bondOptions.length > 1 && (
              <div className="form-group mb-1">
                <label className="d-block">Bond Type Preference</label>
                {bondOptions.map((bondType) => (
                  <div className="form-check form-check-inline" key={bondType}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name={`standardMultiBondType${index}`}
                      id={`standardMultiBondType${bondType}${index}`}
                      value={bondType}
                      checked={row.bondType === bondType}
                      onChange={(event) => {
                        setField("bondType", event.target.value);
                        setField("bondTypePreferenceProvided", true);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`standardMultiBondType${bondType}${index}`}
                    >
                      {bondType}
                    </label>
                  </div>
                ))}
                {existingRequestAction === "Risk Tolerance Update" && (
                  <>
                    <small className="form-text text-muted">
                      Optional. If blank, the bond preference on file remains in effect.
                    </small>
                    {row.bondType && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => {
                          setField("bondType", "");
                          setField("bondTypePreferenceProvided", false);
                        }}
                      >
                        Clear selection
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {issue && (
              <small className="form-text text-danger" role="status">
                {issue}
              </small>
            )}
          </div>
        );
      })}

      {!identityReadOnly && (
        <button
          type="button"
          className="btn btn-outline-primary mb-2"
          onClick={onAddRegistration}
        >
          + Add Registration
        </button>
      )}
    </div>
  );
};

export default StandardMultiRegistrationEditor;
